import React, { useState, useRef, useEffect } from 'react';
import Button from '@mui/material/Button';
import { DataGrid } from '@mui/x-data-grid';
import { uploadBulkFlagAsset } from '../../../services/asset.service';
import { getDepartmentList } from '../../../services/user.service';
import { getTagDivisionList } from '../../../services/master.service';
import * as CryptoJS from "crypto-js";

/* ─── Same CSS as AssetInfo (Bulk Allocation) ─── */
import '../../ExcelUploads/AssetInfo/AssetInfo';
import InfoIcon from '@mui/icons-material/Info';

const templatePath = "/exceltemplates/Flag_Asset_Template.xlsx";
const download_img_Url = "/new-download.gif";

/* ── SVG Download Icon ── */
const DownloadSVG = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
    <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
    <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
  </svg>
);

/* ── Loading Overlay ── */
const LoadingOverlay = () => (
  <div className="loading-overlay">
    <div className="loading-card">
      <div className="loading-spinner">
        <img src={download_img_Url} alt="Uploading" />
      </div>
      <h5 className="loading-title">Uploading Flag Assets…</h5>
      <p className="loading-subtitle">
        <i className="bi bi-info-circle-fill" />
        Please don&apos;t refresh the page until the process is completed
      </p>
    </div>
  </div>
);

/* ── DataGrid Columns ── */
const purpose_columns = [
  { field: "tag_division_name", editable: false, hide: false, headerName: "Purpose Name", width: 350 },
  { field: "tag_division_short_name", editable: false, hide: true, headerName: "Purpose Short Name", width: 350 },
  { field: "purpose_of_flaging", editable: false, hide: true, headerName: "Purpose of Flagging", width: 350 },
];
const control_department_columns = [
  { field: "department_name", editable: false, hide: true, headerName: "Control Department Name", width: 350 },
  { field: "department_short_name", editable: false, hide: false, headerName: "Control Department Short Name", width: 350 },
];

function BulkFlagAsset() {
  const errRef = useRef();
  const [errMsg, setErrMsg] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [purposeItems, setPurposeItems] = useState([]);
  const [control_department_items, setControl_Department_Items] = useState([]);

  useEffect(() => {
    let EncryptData = CryptoJS.AES.encrypt(JSON.stringify({
      'authToken': sessionStorage.getItem('LoggedInToken'),
    }), process.env.REACT_APP_ENCRYPT_SECRET_KEY).toString();
    let oData = { 'token': EncryptData };
    getTagDivisionList(oData).then(res => {
      if (res?.data?.data?.length) setPurposeItems(res.data.data);
    });
    getDepartmentList(oData).then(res => {
      if (res?.data?.data?.length) setControl_Department_Items(res.data.data);
    });
  }, []);

  const onChangeFile = async (event) => {
    event.preventDefault();
    if (event.target.files[0]?.name.split('.').pop() === 'undefined' || event.target.files[0]?.name.split('.').pop() !== 'xlsx') {
      setErrMsg("Please upload valid format. Excel file only allowed.");
      return false;
    } else {
      try {
        const data = new FormData();
        data.append('file', event.target.files[0]);
        data.append('company_id', sessionStorage.getItem('LoggedInCompanyId'));
        data.append('division_id', sessionStorage.getItem('LoggedInDivisionId'));
        data.append('division_code', sessionStorage.getItem('LoggedInDivisionCode'));
        data.append('created_by', sessionStorage.getItem('LoggedInUserId'));

        setLoading('..');
        uploadBulkFlagAsset(data).then(res => {
          if ((typeof (res.data.errors) !== "undefined") && res.data.errors.errno > 0) {
            setErrMsg(res.data.message); setLoading(false); setSuccess(false);
          } else {
            setSuccess(res.data.message); setLoading(false);
          }
        });
      } catch (error) {
        setLoading(false);
        console.error(error);
        setErrMsg(error.data.message);
      }
    }
  };

  /* ─── Instructions data ─── */
  const instructions = [
    { title: 'Date Format:', body: <>Always use <code>YYYY-MM-DD</code> format for dates</> },
    { title: 'Empty Cells:', body: <>Use <code>NA</code> for text and <code>0</code> for numbers</> },
    { title: 'Company Name:', body: <>Use <code>{sessionStorage.getItem('LoggedInCompanyName')}</code> and for branch name use <code>{sessionStorage.getItem('LoggedInDivisionName')}</code></> },
    { title: 'TAG Branch:', body: <>Use <code>{sessionStorage.getItem('LoggedInDivisionShortName')}</code></> },
    { title: 'Dropdown Values:', body: <>For dropdown fields in the form, use only the values provided in the template</> },
  ];

  /* ════════════════════════════
     RENDER
     ════════════════════════════ */
  return (
    <div className="asset-info-container">

      {/* ── Loading Overlay ── */}
      {loading && <LoadingOverlay />}

      {/* ════════════════════════════
                PAGE HEADER
                ════════════════════════════ */}
      <div className="page-header">
        <div className="header-content">
          <div className="header-left">
            <div className="header-icon">
              <i className="bi bi-flag-fill" style={{ fontSize: '1.6rem' }} />
            </div>
            <div>
              <h1 className="page-title">Bulk Flag Asset</h1>
              <p className="page-subtitle">Upload multiple flag asset records using Excel template</p>
            </div>
          </div>
          <a href={'/flag-listing-out'} className="btn-outline-primary">
            <i className="bi bi-list-ul" /> Flag Asset List
          </a>
        </div>

        {/* Alert banners */}
        {success ? (
          <div className="alert-success-modern" role="alert">
            <i className="bi bi-check-circle-fill" /> {success}
          </div>
        ) : errMsg ? (
          <div className="alert-danger-modern" role="alert" ref={errRef}>
            <i className="bi bi-exclamation-triangle-fill" /> {errMsg}
          </div>
        ) : null}
      </div>

      {/* ════════════════════════════
                UPLOAD CARD
                ════════════════════════════ */}
      <div className="upload-cards-grid">
        <div className="upload-card parent-card" id="bulk-flag-upload-card">
          <div className="card-header">
            <div className="card-icon parent-icon">
              <i className="bi bi-file-earmark-spreadsheet" style={{ fontSize: '1.4rem' }} />
            </div>
            <div className="card-header-text">
              <h4>Flag Asset Upload</h4>
              <p>Upload the filled Excel file to flag assets in bulk</p>
            </div>
          </div>
          <div className="card-body">
            <div className="upload-area" id="flag-upload-area">
              <Button
                variant="contained"
                component="label"
                className="upload-button"
                id="flag-upload-btn"
              >
                <i className="bi bi-cloud-upload" style={{ marginRight: 8 }} />
                Upload Flag Asset Excel
                <input hidden type="file" onChange={onChangeFile} />
              </Button>
            </div>

            {loading && (
              <div style={{ textAlign: 'center', fontSize: '0.82rem', color: '#0e8aca', fontWeight: 500, marginTop: 8 }}>
                <span className="spinner-border spinner-border-sm me-2" role="status" />
                Processing upload…
              </div>
            )}

            <div className="template-download">
              <a href={templatePath} className="download-link" id="flag-template-link">
                <DownloadSVG /> Download Flag Asset Template
              </a>
            </div>
          </div>
        </div>
      </div>{/* /upload-cards-grid */}

      {/* ════════════════════════════
                INSTRUCTIONS
                ════════════════════════════ */}
      <div className="instructions-section" id="instructions-section">
        <div className="instructions-header">
          <InfoIcon style={{ fontSize: '1.5rem', color: '#0084ff' }} />
          <h4>Instructions for Bulk Upload</h4>
        </div>
        <div className="instructions-grid">
          {instructions.map((item, idx) => (
            <div className="instruction-item" key={idx} id={`instruction-item-${idx + 1}`}>
              <div className="instruction-number">{idx + 1}</div>
              <div className="instruction-content">
                <strong>{item.title}</strong> {item.body}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ════════════════════════════
                REFERENCE DATA GRIDS
                ════════════════════════════ */}
      <div className="reference-section" id="reference-section">
        <h4 className="reference-title">
          <i className="bi bi-table" style={{ color: '#0084ff' }} /> Reference Data
        </h4>
        <p className="reference-subtitle">Use these values in their respective columns of the Excel template</p>
        <div className="data-grid-container">
          <div className="grid-card">
            <h5 className="grid-card-title"><i className="bi bi-bullseye" /> Purpose</h5>
            <DataGrid
              rows={purposeItems}
              getRowId={(row) => row.id}
              columns={purpose_columns}
              pageSize={5}
              autoHeight
              rowsPerPageOptions={[5]}
            />
          </div>
          <div className="grid-card">
            <h5 className="grid-card-title"><i className="bi bi-building" /> Control Department</h5>
            <DataGrid
              rows={control_department_items}
              getRowId={(row) => row.id}
              columns={control_department_columns}
              pageSize={5}
              autoHeight
              rowsPerPageOptions={[5]}
            />
          </div>
        </div>
      </div>

    </div>
  );
}

export default BulkFlagAsset;
